import { Injectable, inject, effect } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MortgageStageType } from '@app/interfaces/mortgage-stage-type.interface';
import { UsersService } from '@app/services/users.service';
import { ApiService } from '@app/services/api.service';
import { SimpleDealStage } from '@app/enums/simple-deal-stage.enum';
import { Params } from '@feathersjs/feathers';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

@Injectable({ providedIn: 'root' })
export class MortgagesListService extends ApiService<MortgageInterface> {
    private readonly usersService = inject(UsersService);

    public static getSimpleDealStage(mortgage: MortgageInterface): SimpleDealStage {
        if (mortgage.isCanceled || mortgage.isPostponed) {
            return 'cancelled';
        }

        const dealStageMapping: Record<SimpleDealStage, MortgageStageType[]> = {
            active: [
                'init',
                'assigned',
                'create',
                'created',
                'sent',
                'applicationDocuments',
                'applicationFilling',
                'applicationSent',
                'application',
                'approvalDocuments',
                'approval',
                'approved',
                'signed',
                'drawing',
            ],
            drawdown: ['drawdown'],
            cancelled: ['postponed', 'canceled'],
            finalized: ['assignFailed', 'sold', 'scoringKO'],
        };

        for (const [stage, stages] of Object.entries(dealStageMapping)) {
            if (stages.includes(mortgage.stage as MortgageStageType)) {
                return stage as SimpleDealStage;
            }
        }

        return 'active';
    }

    public deal: MortgageInterface = null;
    protected serviceName = 'cases/mortgage';
    private $deal: BehaviorSubject<MortgageInterface> = new BehaviorSubject(null);
    public deal$: Observable<MortgageInterface> = this.$deal.asObservable();
    public isReady = false;

    constructor() {
        super();
        this.initConnection();

        effect(() => {
            if (this.usersService.authenticated()) {
                if (this.loadDebounce) {
                    clearTimeout(this.loadDebounce);
                }

                this.loadDebounce = setTimeout(() => {
                    if (!this.items?.length) {
                        this.load();
                    }
                }, 100);
            } else {
                this.itemsSubject.next([]);
                this.$deal.next(null);
            }
        });
    }

    public async load() {
        try {
            this.isLoading = true;

            // @API deals.find
            const deals = await this.find({
                query: {
                    $sort: { createdAt: -1 },
                    $limit: '-1',
                },
            });

            this.items = (deals as MortgageInterface[])?.map((d) => this.transformDeal(d));
            this.items.sort((a, b) => a.isDelayed - b.isDelayed);
            this.itemsSubject.next(this.items?.length ? this.items : []);
        } catch (error) {
            console.error(error);
            this.items = [];
            this.itemsSubject.next(this.items);
        } finally {
            this.isLoading = false;
            this.isReady = true;
        }
    }

    public setDeal(deal: MortgageInterface): MortgageInterface {
        this.deal = deal ? this.transformDeal(deal) : null;
        this.$deal.next(this.deal);
        return this.deal;
    }

    public transformDeal(mortgage: MortgageInterface): MortgageInterface {
        const modelations = mortgage.modelations?.map((modelation) => modelation.amount);
        mortgage.highestAmountForDisplay = 0;
        if (modelations?.length) {
            mortgage.highestAmountForDisplay = Math.max(...modelations);
        }
        return mortgage;
    }

    protected _updated(item: MortgageInterface): void {
        const index = this.items.findIndex((i) => i._id === item._id);
        if (index > -1) {
            this.items[index] = this.transformDeal(item);
            this.itemsSubject.next(this.items);
        }
        if (this.deal && this.deal._id === item._id) {
            this.setDeal(item);
        }
    }

    public find(params?: Params): Promise<MortgageInterface | MortgageInterface[]> {
        return super
            .find(params)
            .then((deals: MortgageInterface | MortgageInterface[]) => {
                return deals;
            })
            .catch((error) => {
                throw error;
            });
    }
}
